/**
 * The rules behind the Training Overview (#5456, phase 3): which cell each exercise gets in each
 * logged session, which cells are marked, and how the cells are laid out in tables.
 *
 * A coach used to copy a client's sets into a spreadsheet: a row per exercise, a column per
 * session, and a colour where nothing moved. This builds that sheet from the logged workouts.
 *
 * One example used throughout. BORI benches on heavy and light days:
 *
 *   Aug 17 heavy  80 x 6      no mark (first heavy session in view)
 *   Aug 31 heavy  82.5 x 5    no mark: the weight went UP, even with fewer reps
 *   Sep 14 heavy  82.5 x 5    "=" the same weight and reps as the last heavy session
 *   Sep 28 heavy  82.5 x 4    "▼" the same weight and fewer reps
 *
 * Only the FIRST performed set of a session decides, and a session is compared only with the
 * previous session at the same intensity. The rule is deliberately NOT the app's plateau
 * detector: it is the coach's spreadsheet rule, chosen by the owner.
 *
 * Pure: no Angular and no database, so a unit test can drive it (`training-overview.test.ts`).
 * The loading is `training-overview-data.service.ts`; the drawing is `components/cs-training-overview`.
 * See design-docs/coaching-dashboard.md, "Phase 3: the Training Overview".
 */

import { FitnessPlan, IntensityLevel } from '@cf-shared/data-model/fitness-plan';
import {
  Log_Workout,
  Log_WorkoutExercise,
  Log_WorkoutSet,
  combinedEntryForExercise,
  isSessionSteppedDownByDeload,
  isStrengthTestSetType,
} from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';

/**
 * How many days of logs the Training Overview loads first, and how many more each widening adds.
 * 84 (12 weeks) and not 91: right after sign-in a device holds the logs of the last 3 calendar
 * months, which is as little as 89 days, and a read that starts before them waits on the network.
 */
export const TRAINING_OVERVIEW_WINDOW_DAYS = 84;
/** how many sessions each table shows first, and how many "Show earlier" adds */
export const TRAINING_OVERVIEW_SESSIONS_STEP = 10;
/** shown in place of a sets count or rep target that does not exist */
export const TRAINING_OVERVIEW_NO_VALUE = '–';

/** how a cell's first set compares with the previous session at the same intensity */
export enum OverviewMark {
  None = 'None',
  /** the same weight and the same reps */
  Same = 'Same',
  /** a lower weight, or the same weight and fewer reps */
  Down = 'Down',
}

/** a session the app made different on purpose; such a cell is tagged and never marked */
export enum OverviewSessionTag {
  None = 'None',
  /** the first set was a strength or rep test */
  Test = 'Test',
  /** the weight is lower because the user accepted a deload */
  Deload = 'Deload',
  /** the first session after a training break, prescribed lighter */
  Break = 'Break',
  /** the app held the prescribed weight below the program's own target (#5176) */
  Cap = 'Cap',
}

/** which day of an exercise that alternates heavy and light days this was */
export enum OverviewIntensityTag {
  None = 'None',
  Heavy = 'Heavy',
  Medium = 'Medium',
  Light = 'Light',
}

export enum OverviewCellKind {
  /** the session holds nothing for this exercise */
  Empty = 'Empty',
  /** the session holds the exercise and no performed set */
  Skipped = 'Skipped',
  Trained = 'Trained',
}

/** one exercise in one session */
export interface OverviewCell {
  kind: OverviewCellKind;
  /** every performed set, as text: "82.5 kg × 5, 4, 4". Empty unless Trained */
  setsText: string;
  /** the weight alone, when every performed set used the same weight; else empty */
  weightText: string;
  /** the reps alone, when every performed set used the same weight; else empty */
  repsText: string;
  intensityTag: OverviewIntensityTag;
  sessionTag: OverviewSessionTag;
  mark: OverviewMark;
}

/** one exercise's row in one table */
export interface OverviewRow {
  exerciseId: string;
  /** the exercise's name; filled in by the loader, which is the one that can look names up */
  name: string;
  /** true for an exercise that is in this workout of the program today */
  isInProgram: boolean;
  /** true when the exercise no longer exists (a deleted custom exercise); filled in by the loader */
  isExerciseMissing: boolean;
  /** the program entry of a program row; absent on a grey row */
  subSlotId?: string;
  /** the planned number of sets, as text; filled in by the loader for a program row */
  setsText?: string;
  /** the rep target, as text; filled in by the loader for a program row */
  repTargetText?: string;
  /** one cell per shown column of the section, in column order */
  cells: OverviewCell[];
}

/** one column: a logged session */
export interface OverviewColumn {
  workoutId: string;
  /** the session's local calendar day, as an ISO date */
  dateIso: string;
}

/** one table: a workout of the program, or the sessions that belong to none */
export interface OverviewSection {
  /** the workout's index in the program; undefined for "Other sessions" */
  planReference: number | undefined;
  /** the workout's name; filled in by the loader. Empty for "Other sessions" */
  title: string;
  /** the workout's letter, for its badge; filled in by the loader. Empty for "Other sessions" */
  badge: string;
  /** the names of the program rows' exercises, joined; filled in by the loader */
  exerciseNamesText: string;
  /** how many sessions this section has in all, shown or not */
  sessionCount: number;
  /** the shown sessions, oldest first */
  columns: OverviewColumn[];
  /** the exercises in this workout today, in program order */
  programRows: OverviewRow[];
  /** exercises trained in a shown session that are not in this workout today, newest first */
  greyRows: OverviewRow[];
}

export interface TrainingOverviewModel {
  sections: OverviewSection[];
  /** how many program exercises have a newest trained session marked Same */
  latestSameCount: number;
  /** how many program exercises have a newest trained session marked Down */
  latestDownCount: number;
}

/** one logged workout, with the two facts the caller has to resolve for it */
export interface OverviewSessionInput {
  workout: Log_Workout;
  /** which workout of the program it was, resolved by the caller; undefined when unknown */
  planReference: number | undefined;
  /** the session's local calendar day, as an ISO date */
  dateIso: string;
  /** the session's time in milliseconds, for ordering */
  timeMs: number;
}

/** the texts of one trained cell */
export interface OverviewCellTexts {
  setsText: string;
  weightText: string;
  repsText: string;
}

export interface TrainingOverviewInput {
  /** the program: its workouts and their entries. Only `workouts` and `subSlots` are read */
  plan: Pick<FitnessPlan, 'workouts' | 'subSlots'>;
  /** every logged workout in the loaded window, in any order */
  sessions: OverviewSessionInput[];
  /** how many of the newest sessions to show for a section (`undefined` is "Other sessions") */
  shownSessionCount: (planReference: number | undefined) => number;
  /**
   * Turns an entry that holds ONLY the performed sets into the cell's texts. `isSameWeight` is true
   * when every one of those sets used exactly the same weight.
   */
  formatCell: (entry: Log_WorkoutExercise, isSameWeight: boolean) => OverviewCellTexts;
}

const EMPTY_CELL: OverviewCell = Object.freeze({
  kind: OverviewCellKind.Empty,
  setsText: '',
  weightText: '',
  repsText: '',
  intensityTag: OverviewIntensityTag.None,
  sessionTag: OverviewSessionTag.None,
  mark: OverviewMark.None,
});

/** the sets of an entry that were really performed: done, with a real weight and a real rep count */
export function performedSets(entry: Pick<Log_WorkoutExercise, 'sets'> | undefined): Log_WorkoutSet[] {
  return (entry?.sets ?? []).filter((set): set is Log_WorkoutSet =>
    !!set
    && set.state === LogRecordState.Done
    && Number.isFinite(set.stats?.reps)
    && Number.isFinite(set.stats?.weight_kg));
}

/** which day of a heavy/light exercise a stored intensity level is */
export function intensityTagFor(pathLevel: IntensityLevel | undefined): OverviewIntensityTag {
  switch (pathLevel) {
    case IntensityLevel.DUPheavy:
    case IntensityLevel.TUPheavy:
      return OverviewIntensityTag.Heavy;
    case IntensityLevel.TUPmedium:
      return OverviewIntensityTag.Medium;
    case IntensityLevel.DUPlight:
    case IntensityLevel.TUPlight:
      return OverviewIntensityTag.Light;
    default:
      return OverviewIntensityTag.None;
  }
}

/** the first set of the last trained, non-test session of one exercise at one intensity */
interface Baseline {
  /** every set of that session's entry, for the deload question */
  sets: Log_WorkoutSet[];
  weight_kg: number;
  reps: number;
}

/** the weight of a first set, rounded to 0.01 kg so two readings of one plate count as equal */
function comparableWeight(set: Log_WorkoutSet): number {
  return Math.round(set.stats.weight_kg * 100) / 100;
}

/**
 * The tag and mark of one trained cell, by the first rule that fits. See the file header for the
 * mark rule. A test session is tagged and never becomes the baseline; a break, a capped weight
 * and a deload are tagged, not marked, and do become the baseline, because the sessions after
 * them are trained from there.
 */
function judgeTrainedCell(
  entry: Log_WorkoutExercise,
  first: Log_WorkoutSet,
  baseline: Baseline | undefined,
): { sessionTag: OverviewSessionTag; mark: OverviewMark; becomesBaseline: boolean } {
  if (isStrengthTestSetType(first.setType)) {
    return { sessionTag: OverviewSessionTag.Test, mark: OverviewMark.None, becomesBaseline: false };
  }
  if (first.layoffPrescribed || first.layoffHeldWeightWithReducedReps) {
    return { sessionTag: OverviewSessionTag.Break, mark: OverviewMark.None, becomesBaseline: true };
  }
  if (first.prescribedWeightCapped) {
    return { sessionTag: OverviewSessionTag.Cap, mark: OverviewMark.None, becomesBaseline: true };
  }
  if (!baseline) {
    return { sessionTag: OverviewSessionTag.None, mark: OverviewMark.None, becomesBaseline: true };
  }
  const weight = comparableWeight(first);
  const reps = first.stats.reps;
  if (weight < baseline.weight_kg) {
    // a lower weight after an accepted deload is the app's own doing, not a step back
    const path = entry.planSubslotBackup?.exercisePaths?.find(p => p.intensityLevel === entry.pathLevel);
    if (isSessionSteppedDownByDeload(baseline.sets, path?.lastDeload1rm_net_kg)) {
      return { sessionTag: OverviewSessionTag.Deload, mark: OverviewMark.None, becomesBaseline: true };
    }
    return { sessionTag: OverviewSessionTag.None, mark: OverviewMark.Down, becomesBaseline: true };
  }
  let mark = OverviewMark.None;
  if (weight === baseline.weight_kg) {
    if (reps < baseline.reps) {
      mark = OverviewMark.Down;
    } else if (reps === baseline.reps) {
      mark = OverviewMark.Same;
    }
  }
  return { sessionTag: OverviewSessionTag.None, mark, becomesBaseline: true };
}

/** the distinct exercise ids a workout's entries carry, in list order */
function exerciseIdsOf(workout: Log_Workout): string[] {
  const ids: string[] = [];
  for (const entry of workout.exercises ?? []) {
    if (entry?.exerciseId && !ids.includes(entry.exerciseId)) {
      ids.push(entry.exerciseId);
    }
  }
  return ids;
}

/** a program row before its cells are filled: the exercise and every program entry of the workout that holds it */
interface ProgramRowSeed {
  exerciseId: string;
  subSlotIds: string[];
}

/**
 * The exercises of one workout of the program, in program order. An entry with no program entry
 * behind it, or with no exercise picked, gets no row: that is a damaged plan, and a row would show
 * history for an exercise the program does not hold. Two entries holding the same exercise share
 * one row.
 */
function programRowSeeds(plan: TrainingOverviewInput['plan'], workoutIndex: number): ProgramRowSeed[] {
  const seeds: ProgramRowSeed[] = [];
  for (const subSlotId of plan.workouts?.[workoutIndex]?.subSlotIds ?? []) {
    const exerciseId = plan.subSlots?.[subSlotId]?.exercise_id;
    if (!exerciseId) continue;
    const existing = seeds.find(seed => seed.exerciseId === exerciseId);
    if (existing) {
      existing.subSlotIds.push(subSlotId);
    } else {
      seeds.push({ exerciseId, subSlotIds: [subSlotId] });
    }
  }
  return seeds;
}

/** every judged cell, and for each exercise the newest one in which it was trained */
interface JudgedCells {
  /** the cell of one exercise in one session; absent when the session holds nothing for it under its id */
  cellOf(workoutId: string, exerciseId: string): OverviewCell | undefined;
  newestTrainedCellByExercise: Map<string, OverviewCell>;
}

/** the cell of one exercise in one session, and what the session after it is compared with */
function judgeEntry(
  entry: Log_WorkoutExercise,
  baseline: Baseline | undefined,
  formatCell: TrainingOverviewInput['formatCell'],
): { cell: OverviewCell; newBaseline: Baseline | undefined } {
  const done = performedSets(entry);
  const intensityTag = intensityTagFor(entry.pathLevel);
  // if no set was performed, the exercise was skipped; a skipped session changes no baseline
  if (done.length === 0) {
    return { cell: { ...EMPTY_CELL, kind: OverviewCellKind.Skipped, intensityTag }, newBaseline: undefined };
  }
  const first = done[0];
  const verdict = judgeTrainedCell(entry, first, baseline);
  const isSameWeight = done.every(set => set.stats.weight_kg === first.stats.weight_kg);
  const cell: OverviewCell = {
    kind: OverviewCellKind.Trained,
    ...formatCell({ ...entry, sets: done }, isSameWeight),
    intensityTag,
    sessionTag: verdict.sessionTag,
    mark: verdict.mark,
  };
  const newBaseline = verdict.becomesBaseline
    ? { sets: entry.sets ?? [], weight_kg: comparableWeight(first), reps: first.stats.reps }
    : undefined;
  return { cell, newBaseline };
}

/**
 * Walks every exercise's sessions oldest to newest, over ALL workouts and not one table's, and
 * judges each cell. An exercise that is in two workouts is therefore compared across both.
 */
function judgeAllCells(sessions: OverviewSessionInput[], formatCell: TrainingOverviewInput['formatCell']): JudgedCells {
  const cellBySessionAndExercise = new Map<string, OverviewCell>();
  const baselineByExerciseAndLevel = new Map<string, Baseline>();
  const newestTrainedCellByExercise = new Map<string, OverviewCell>();
  const cellKey = (workoutId: string, exerciseId: string) => `${workoutId}|${exerciseId}`;
  // every exercise of every session, oldest session first
  const visits = sessions.flatMap(session => exerciseIdsOf(session.workout).map(exerciseId => ({ session, exerciseId })));
  for (const { session, exerciseId } of visits) {
    // a split entry (#5507) is read as one, in the order its sets were performed
    const entry = combinedEntryForExercise(session.workout.exercises, exerciseId);
    if (!entry) continue;
    const baselineKey = `${exerciseId}|${entry.pathLevel ?? 'none'}`;
    const { cell, newBaseline } = judgeEntry(entry, baselineByExerciseAndLevel.get(baselineKey), formatCell);
    cellBySessionAndExercise.set(cellKey(session.workout._id, exerciseId), cell);
    if (newBaseline) {
      baselineByExerciseAndLevel.set(baselineKey, newBaseline);
    }
    if (cell.kind === OverviewCellKind.Trained) {
      newestTrainedCellByExercise.set(exerciseId, cell);
    }
  }
  return {
    cellOf: (workoutId, exerciseId) => cellBySessionAndExercise.get(cellKey(workoutId, exerciseId)),
    newestTrainedCellByExercise,
  };
}

/** the cell of one program row in one session: by exercise id, else "skipped" for an entry that was never started */
function programCell(seed: ProgramRowSeed, session: OverviewSessionInput, judged: JudgedCells): OverviewCell {
  const byId = judged.cellOf(session.workout._id, seed.exerciseId);
  if (byId) return byId;
  // an exercise the user never started is stored with its program entry and no exercise id
  const isNeverStarted = (session.workout.exercises ?? []).some(entry =>
    !!entry && !entry.exerciseId && seed.subSlotIds.includes(entry.subSlotId));
  return isNeverStarted ? { ...EMPTY_CELL, kind: OverviewCellKind.Skipped } : EMPTY_CELL;
}

/** the exercises trained in a shown session that have no program row, the most recently trained first */
function greyExerciseIds(shown: OverviewSessionInput[], seeds: ProgramRowSeed[], judged: JudgedCells): string[] {
  const programIds = new Set(seeds.map(seed => seed.exerciseId));
  const ids: string[] = [];
  const trainedNewestFirst = [...shown].reverse().flatMap(session =>
    exerciseIdsOf(session.workout).filter(exerciseId => judged.cellOf(session.workout._id, exerciseId)?.kind === OverviewCellKind.Trained));
  for (const exerciseId of trainedNewestFirst) {
    if (!programIds.has(exerciseId) && !ids.includes(exerciseId)) {
      ids.push(exerciseId);
    }
  }
  return ids;
}

/**
 * One table: the sessions that belong to it, the newest of them as columns, and its rows.
 *
 * @param ownSessions every session of this table, oldest first
 * @param seeds the program rows; empty for "Other sessions", where every row is grey
 */
function buildSection(
  planReference: number | undefined,
  ownSessions: OverviewSessionInput[],
  seeds: ProgramRowSeed[],
  shownSessionCount: number,
  judged: JudgedCells,
): OverviewSection {
  const shownCount = Math.max(0, Math.floor(shownSessionCount));
  const shown = shownCount > 0 ? ownSessions.slice(-shownCount) : [];
  const programRows: OverviewRow[] = seeds.map(seed => ({
    exerciseId: seed.exerciseId,
    name: '',
    isInProgram: true,
    isExerciseMissing: false,
    subSlotId: seed.subSlotIds[0],
    cells: shown.map(session => programCell(seed, session, judged)),
  }));
  const greyRows: OverviewRow[] = greyExerciseIds(shown, seeds, judged).map(exerciseId => ({
    exerciseId,
    name: '',
    isInProgram: false,
    isExerciseMissing: false,
    cells: shown.map(session => judged.cellOf(session.workout._id, exerciseId) ?? EMPTY_CELL),
  }));
  return {
    planReference,
    title: '',
    badge: '',
    exerciseNamesText: '',
    sessionCount: ownSessions.length,
    columns: shown.map(session => ({ workoutId: session.workout._id, dateIso: session.dateIso })),
    programRows,
    greyRows,
  };
}

/** Builds the whole model: every section, its shown columns, its rows and the two counts. */
export function buildTrainingOverview(input: TrainingOverviewInput): TrainingOverviewModel {
  const workoutCount = input.plan.workouts?.length ?? 0;
  // a workout is a session when at least one of its exercises has a performed set; its state does not matter
  const sessions = input.sessions
    .filter(session => (session.workout.exercises ?? []).some(entry => performedSets(entry ?? undefined).length > 0))
    .sort((a, b) => a.timeMs - b.timeMs);
  const judged = judgeAllCells(sessions, input.formatCell);

  // one section per workout of the program, then "Other sessions" for every session that belongs to none
  const isKnownReference = (ref: number | undefined): ref is number =>
    typeof ref === 'number' && Number.isInteger(ref) && ref >= 0 && ref < workoutCount;
  const sections: OverviewSection[] = [];
  const programExerciseIds = new Set<string>();
  for (let index = 0; index < workoutCount; index++) {
    const seeds = programRowSeeds(input.plan, index);
    seeds.forEach(seed => programExerciseIds.add(seed.exerciseId));
    const ownSessions = sessions.filter(session => session.planReference === index);
    sections.push(buildSection(index, ownSessions, seeds, input.shownSessionCount(index), judged));
  }
  const otherSessions = sessions.filter(session => !isKnownReference(session.planReference));
  // "Other sessions" is left out when no session belongs there
  if (otherSessions.length > 0) {
    sections.push(buildSection(undefined, otherSessions, [], input.shownSessionCount(undefined), judged));
  }

  // the two counts: each program exercise once, by the mark of its newest trained session
  const newestMarks = [...programExerciseIds].map(exerciseId => judged.newestTrainedCellByExercise.get(exerciseId)?.mark);
  return {
    sections,
    latestSameCount: newestMarks.filter(mark => mark === OverviewMark.Same).length,
    latestDownCount: newestMarks.filter(mark => mark === OverviewMark.Down).length,
  };
}

/**
 * The texts of one trained cell, from the set text the app's own formatter wrote ("82.5 kg × 5, 4,
 * 4"). When every set used one weight the text has exactly one weight in it, so it is split at the
 * formatter's own separator into the weight ("82.5 kg") and the reps ("5, 4, 4").
 *
 * @param timesSeparator the separator the formatter writes between a weight and its reps
 */
export function cellTexts(setsText: string, isSameWeight: boolean, timesSeparator: string): OverviewCellTexts {
  const separatorAt = isSameWeight ? setsText.indexOf(timesSeparator) : -1;
  if (separatorAt < 0) {
    return { setsText, weightText: '', repsText: '' };
  }
  return {
    setsText,
    weightText: setsText.slice(0, separatorAt),
    repsText: setsText.slice(separatorAt + timesSeparator.length),
  };
}

/** a zero-width space: a point where a line may break, drawn as nothing */
export const OVERVIEW_BREAK_POINT = '\u200b';

/**
 * A set text with a line break point after the times sign and after each comma between reps. The
 * formatter joins a number and its unit, the times sign and each ", " with no-break spaces ("112.5
 * kg x 22, 20, 20, 18"), so on a phone a long set line had nowhere to break and ran past its card
 * (#6691). With these break points it wraps there, and only there: a number and a weight with its
 * unit stay whole.
 *
 * @param commaSeparator what the formatter writes between two reps of one weight
 * @param timesSeparator the separator the formatter writes between a weight and its reps
 */
export function breakableSetsText(text: string, commaSeparator: string, timesSeparator: string): string {
  return text.split(commaSeparator).join(commaSeparator + OVERVIEW_BREAK_POINT)
    .split(timesSeparator).join(timesSeparator + OVERVIEW_BREAK_POINT);
}

/** what one press on "Show earlier" changes */
export interface ShowEarlierStep {
  /** how many sessions the section shows after the press */
  shownCount: number;
  /** how many days of logs are loaded after the press */
  windowDays: number;
}

/**
 * What "Show earlier" does for one section. Sessions that are already loaded are shown first.
 * Only when they cannot fill the step, and an older finished workout is known to exist, is the
 * loaded window widened as well.
 *
 * BORI's Workout A shows 10 of 23 loaded sessions: a press shows 20 and loads nothing more. At 20
 * of 23, with older workouts on the device, a press asks for 30 and widens the window from 84 to
 * 168 days. At 20 of 23 with nothing older, a press shows the 3 that are left.
 */
export function showEarlierStep(
  section: Pick<OverviewSection, 'sessionCount' | 'columns'>,
  mayHaveOlder: boolean,
  windowDays: number,
): ShowEarlierStep {
  const notShownYet = section.sessionCount - section.columns.length;
  const needsOlderLogs = notShownYet < TRAINING_OVERVIEW_SESSIONS_STEP && mayHaveOlder;
  return {
    shownCount: section.columns.length + TRAINING_OVERVIEW_SESSIONS_STEP,
    windowDays: needsOlderLogs ? windowDays + TRAINING_OVERVIEW_WINDOW_DAYS : windowDays,
  };
}

/**
 * The range read answered with no workout at all although a finished workout exists inside the
 * range. The cache behind the range read answers with an empty list when it was cleared while
 * being filled (Edit Workouts' save and the coach's Apply both clear it), so an empty answer is
 * checked once and, when it stays empty, reported as a failed load and never shown as "no logged
 * workouts".
 */
export class TrainingOverviewEmptyReadError extends Error {
  constructor() {
    super('training overview: the log range read came back empty although a finished workout is inside the range');
    this.name = 'TrainingOverviewEmptyReadError';
  }
}

/**
 * Reads the workouts of a time range, checking an empty answer once.
 *
 * @param readRange reads the workouts of the range; called a second time when the first answer is empty
 * @param latestFinishedTimeMs the time of the newest finished workout on the device, or undefined when there is none
 * @param startMs, endMs the range, in milliseconds
 */
export async function readWorkoutsCheckingEmptyAsync<T>(
  readRange: () => Promise<T[]>,
  latestFinishedTimeMs: () => Promise<number | undefined>,
  startMs: number,
  endMs: number,
): Promise<T[]> {
  const first = await readRange();
  if (first.length > 0) {
    return first;
  }
  const latest = await latestFinishedTimeMs();
  // if no finished workout lies inside the range, the empty answer is the truth
  if (latest === undefined || !Number.isFinite(latest) || latest < startMs || latest > endMs) {
    return first;
  }
  const second = await readRange();
  if (second.length === 0) {
    throw new TrainingOverviewEmptyReadError();
  }
  return second;
}
